import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { formatInputValue, getNumericInputProps } from './utils/inputHelpers.js';
import CalculationTransparency from './components/CalculationTransparency.js';

const SMSFFHSSCalculator = ({ smsfState, setSmsfState }) => {
    const [fhssData, setFhssData] = useState({
        // Voluntary Concessional Contributions
        year1CC: 15000,
        year2CC: 15000,
        year3CC: 15000,
        year4CC: 0,
        year5CC: 0,
        // Non-Concessional Contributions
        year1NCC: 0,
        year2NCC: 0,
        year3NCC: 0,
        year4NCC: 0,
        year5NCC: 0,
        withdrawalTaxRate: 0.30 // Marginal tax rate
    });

    const [results, setResults] = useState(null);

    const FHSS_LIMITS = {
        MAX_TOTAL: 50000,
        MAX_PER_YEAR: 15000,
        MIN_YEARS: 1,
        TAX_OFFSET: 0.30, // 30% tax offset on withdrawal
        ELIGIBLE_USES: ['First home deposit', 'Home purchase costs']
    };

    const calculateFHSS = () => {
        const totalCC = fhssData.year1CC + fhssData.year2CC + fhssData.year3CC + fhssData.year4CC + fhssData.year5CC;
        const totalNCC = fhssData.year1NCC + fhssData.year2NCC + fhssData.year3NCC + fhssData.year4NCC + fhssData.year5NCC;
        const totalContributions = totalCC + totalNCC;
        
        // Calculate years of contributions (non-zero years)
        const yearsContributed = [1,2,3,4,5].filter(y => 
            (fhssData[`year${y}CC`] > 0 || fhssData[`year${y}NCC`] > 0)
        ).length;
        
        const maxReleasable = Math.min(totalContributions, FHSS_LIMITS.MAX_TOTAL);
        const deemedEarnings = maxReleasable * 0.05; // Simplified 5% deemed earnings
        const withheldTax = totalCC * FHSS_LIMITS.TAX_OFFSET; // 30% tax on concessional
        const netAmount = maxReleasable + deemedEarnings - withheldTax;
        
        setResults({
            totalCC,
            totalNCC,
            totalContributions,
            yearsContributed,
            maxReleasable,
            deemedEarnings: Math.round(deemedEarnings),
            withheldTax: Math.round(withheldTax),
            netAmount: Math.round(netAmount),
            eligible: totalContributions <= FHSS_LIMITS.MAX_TOTAL
        });
    };

    return (
        <div className="max-w-5xl mx-auto p-6">
            <h2 className="text-3xl font-bold mb-2">First Home Super Saver (FHSS)</h2>
            <p className="text-gray-600 mb-6">Calculate how much you can save for your first home</p>

            <div className="space-y-6">
                <div className="bg-blue-50 p-4 rounded-lg">
                    <p className="text-sm text-blue-800">
                        Save up to $50,000 in super for your first home. Withdraw with 30% tax offset. Maximum $15,000 per year.
                    </p>
                </div>

                {/* Voluntary Concessional Contributions */}
                <div className="bg-white p-6 rounded-lg shadow">
                    <h3 className="font-semibold mb-4">Voluntary Concessional Contributions (Taxed at 15%)</h3>
                    <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                        {[1, 2, 3, 4, 5].map(year => (
                            <div key={year}>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Year {year} ($)</label>
                                <input
                                    {...getNumericInputProps()}
                                    max={FHSS_LIMITS.MAX_PER_YEAR}
                                    value={fhssData[`year${year}CC`] || ''}
                                    onChange={(e) => setFhssData({ ...fhssData, [`year${year}CC`]: Number(e.target.value) || 0 })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                />
                                {(fhssData[`year${year}CC`] > FHSS_LIMITS.MAX_PER_YEAR) && (
                                    <p className="text-red-600 text-xs mt-1">Max $15,000/yr</p>
                                )}
                            </div>
                        ))}
                    </div>
                </div>

                {/* Non-Concessional Contributions */}
                <div className="bg-white p-6 rounded-lg shadow">
                    <h3 className="font-semibold mb-4">Non-Concessional Contributions (After-Tax)</h3>
                    <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                        {[1, 2, 3, 4, 5].map(year => (
                            <div key={year}>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Year {year} ($)</label>
                                <input
                                    {...getNumericInputProps()}
                                    value={fhssData[`year${year}NCC`] || ''}
                                    onChange={(e) => setFhssData({ ...fhssData, [`year${year}NCC`]: Number(e.target.value) || 0 })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                />
                            </div>
                        ))}
                    </div>
                </div>

                {/* Calculate Button */}
                <button
                    type="button"
                    onClick={calculateFHSS}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-8 rounded-lg transition-colors"
                >
                    Calculate FHSS Benefit
                </button>

                {/* Results */}
                {results && (
                    <>
                        <div className="bg-white p-6 rounded-lg shadow">
                            <h3 className="font-semibold mb-4">FHSS Calculation Results</h3>
                            
                            {/* Summary Stats */}
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                                <div className="bg-blue-50 p-4 rounded-lg">
                                    <div className="text-sm text-gray-600 mb-1">Years of Contributions</div>
                                    <div className="text-2xl font-bold text-blue-600">{results.yearsContributed}</div>
                                </div>
                                <div className="bg-green-50 p-4 rounded-lg">
                                    <div className="text-sm text-gray-600 mb-1">Total Voluntary CC</div>
                                    <div className="text-xl font-bold text-green-600">${results.totalCC.toLocaleString()}</div>
                                </div>
                                <div className="bg-purple-50 p-4 rounded-lg">
                                    <div className="text-sm text-gray-600 mb-1">Total NCC</div>
                                    <div className="text-xl font-bold text-purple-600">${results.totalNCC.toLocaleString()}</div>
                                </div>
                                <div className="bg-indigo-50 p-4 rounded-lg">
                                    <div className="text-sm text-gray-600 mb-1">Maximum Releasable</div>
                                    <div className="text-xl font-bold text-indigo-600">${results.maxReleasable.toLocaleString()}</div>
                                    <div className="text-xs text-gray-500">Cap: $50,000</div>
                                </div>
                            </div>

                            {/* Detailed Breakdown */}
                            <div className="space-y-3 border-t pt-4">
                                <div className="flex justify-between">
                                    <span className="text-gray-700">Amount You Can Release:</span>
                                    <span className="font-bold">${results.maxReleasable.toLocaleString()}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-700">Deemed Earnings:</span>
                                    <span className="font-bold text-green-600">+${results.deemedEarnings.toLocaleString()}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-700">Withheld Tax (30% on CC):</span>
                                    <span className="font-bold text-red-600">-${results.withheldTax.toLocaleString()}</span>
                                </div>
                                <div className="flex justify-between border-t pt-3 mt-3">
                                    <span className="text-lg font-semibold text-gray-900">Net Amount for Home Deposit:</span>
                                    <span className="text-2xl font-bold text-blue-600">${results.netAmount.toLocaleString()}</span>
                                </div>
                            </div>
                        </div>

                        {/* Eligibility Checklist */}
                        <div className="bg-white p-6 rounded-lg shadow">
                            <h3 className="font-semibold mb-4">✅ Eligibility Checklist</h3>
                            <div className="space-y-2 text-sm">
                                <div className="flex items-center">
                                    <span className={results.eligible ? 'text-green-600' : 'text-red-600'}>
                                        {results.eligible ? '✅' : '❌'}
                                    </span>
                                    <span className="ml-2">Within $50,000 total limit ({results.eligible ? 'Yes' : 'No'})</span>
                                </div>
                                <div className="flex items-center">
                                    <span className="text-gray-400">ℹ️</span>
                                    <span className="ml-2">Maximum $15,000 per year (check individual years above)</span>
                                </div>
                                <div className="flex items-center">
                                    <span className="text-gray-400">ℹ️</span>
                                    <span className="ml-2">Must be first home buyer</span>
                                </div>
                                <div className="flex items-center">
                                    <span className="text-gray-400">ℹ️</span>
                                    <span className="ml-2">Must not have previously owned property</span>
                                </div>
                                <div className="flex items-center">
                                    <span className="text-gray-400">ℹ️</span>
                                    <span className="ml-2">Must sign contract within 12 months of release</span>
                                </div>
                                <div className="flex items-center">
                                    <span className="text-gray-400">ℹ️</span>
                                    <span className="ml-2">Must occupy or intend to occupy as soon as practicable</span>
                                </div>
                            </div>
                        </div>

                        <CalculationTransparency
                            kind="smsf"
                            title="FHSS Transparency"
                            inputs={{
                                currentIncome: fhssData.marginalTaxRate,
                                contributionAmount: (results.totalCC || 0) + (results.totalNCC || 0),
                            }}
                            results={{
                                ...results,
                                warnings: results.eligible ? [] : [{ message: 'FHSS limit exceeded on these inputs.' }],
                            }}
                        />
                    </>
                )}

                {!results && (
                    <div className="bg-gray-50 p-8 rounded-lg text-center text-gray-500">
                        <p>Click "Calculate FHSS Benefit" to see results</p>
                    </div>
                )}
            </div>
        </div>
    );
};

SMSFFHSSCalculator.propTypes = {
    smsfState: PropTypes.object.isRequired,
    setSmsfState: PropTypes.func.isRequired
};

export default SMSFFHSSCalculator;

